import AppRoutes from "@/routes/routes";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./redux/store";
import { clearAllState } from "./redux/reducers";
import {
	clearUserInfo,
	postUserLoginByTelegram,
	setNativeUser,
	setToken,
	setUserInfo,
} from "./redux/reducers/authSlice";
import { useLocalStorage } from "./util/Storage";
import { isEmpty } from "./util/common";
import { setSearch } from "@/redux/reducers/initialization";
import useRouter from "./hooks/useRouter";
import { getApiAppConfig } from "./redux/reducers/config";
import { useMetaMask } from "@onyx-p/metamask-react";
import { isDef } from "./util/validate";
import { isString } from "lodash";
import API from "./api";
import { t } from "i18next";
import { Dialog } from "antd-mobile";
import { loginByTelegramData } from "./api/type";
import { showGlobalLoading } from "./components/globalLoading";
import { Path } from "react-router-dom";
import _ from "lodash";

const App = () => {
	const dispatch: AppDispatch = useDispatch();
	const { location } = useRouter();
	const { loginState, isAdminType, userInfo } = useSelector(
		(state: RootState) => state.auth
	);
	const { status, ethereum, account } = useMetaMask();
	const { customNavigate } = useRouter();
	const [initialLocation, updateInitialLocation] = useState<Path>();
	const [isLoggingout, setIsLoggingout] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const { showLoading, hideLoading } = showGlobalLoading();
	const initRef = useRef(false);
	const telegram = window.Telegram?.WebApp;
	const isTelegram = !!telegram?.initData;

	useEffect(() => {
		updateInitialLocation(_.cloneDeep(location));
		dispatch(setSearch(location.search));
		dispatch(clearAllState());
	}, []);

	useEffect(() => {
		dispatch(getApiAppConfig({}));
	}, [loginState]);

	const handleLogout = () => {
		dispatch(clearUserInfo());
		useLocalStorage.clearAll();
		customNavigate("/login", { replace: true });
		setIsLoggingout(false);
	};

	useEffect(() => {
		const handleStatusLogin = async () => {
			showLoading();
			if (!loginState) {
				dispatch(setNativeUser(null));
			}

			// 检查 Telegram WebApp 是否可用并获取用户信息
			if (isTelegram && !initRef.current) {
				try {
					// 获取URL参数中的用户信息
					await dispatch(clearUserInfo());
					useLocalStorage.clearAll();
					initRef.current = true;
					const urlParts = window.location.href.split("?");
					useLocalStorage.set("originalHref", window.location.href);
					let userData = {
						chatCreateName: "",
						chatId: "",
						chatCreate: "",
					};
					if (urlParts.length > 1) {
						const queryParams = new URLSearchParams(urlParts[1]);
						const startApp = queryParams.get("tgWebAppStartParam");
						if (startApp) {
							const str = decodeURIComponent(startApp);
							console.log("路由传参加密串", str);
							const decodedStr = str.split("#/")[0];
							const reqData = atob(decodedStr);
							console.log("解密结果", reqData);
							userData = JSON.parse(reqData);
							console.log("xx:", userData);
						}
					}
					const params: loginByTelegramData = {
						initData: telegram.initData,
						chatCreateNickname: userData.chatCreateName,
						chatId: userData.chatId,
						chatCreate: userData.chatCreate,
					};
					const response = await dispatch(
						postUserLoginByTelegram(params)
					);
					const result = response.payload as any;
					if (result?.code === 0) {
						const data = result.content.data;
						useLocalStorage.set("token", data.token);
						await dispatch(setUserInfo(data.user));
						await dispatch(setToken(data.token)); //这是异步的
						await dispatch(
							setNativeUser({
								name: data.user.nickname,
								avatar: data.user.cover ?? "",
								is_master: false,
							})
						);
					}
				} catch (error) {
					console.error(error);
					handleLogout();
				}
				initRef.current = false;
			} else if (loginState && status === "connected") {
				requestNativeUserInfo();
			}
			if (!initRef.current) {
				hideLoading();
				setIsInitialized(true);
			}
		};
		handleStatusLogin();
	}, [status, loginState]);

	useEffect(() => {
		if (
			isLoggingout ||
			!loginState ||
			isAdminType ||
			isTelegram ||
			!isDef(initialLocation)
		) {
			return;
		}

		if (initialLocation.pathname === "/partnergame") {
			const query = new URLSearchParams(initialLocation.search ?? "");
			const type = query.get("type");
			if (type === "1") {
				return;
			}
		}
		hideLoading();
		let logoutReason: string | undefined;
		if (status === "notConnected") {
			logoutReason = t("NotConnectedLogout");
		} else if (
			status === "connected" &&
			account?.length &&
			account.toLowerCase() !== userInfo?.walletAddress?.toLowerCase()
		) {
			logoutReason = t("AccountWalletMismatchLogout");
		}
		if (logoutReason) {
			setIsLoggingout(true);
			setIsInitialized(true);

			Dialog.alert({
				title: logoutReason,
				confirmText: t("Confirm"),
				onConfirm: () => {
					handleLogout();
				},
			});
		}
	}, [account, status, isAdminType]);

	useEffect(() => {
		if (loginState) {
			const query = new URLSearchParams(initialLocation?.search ?? "");
			const channelCode = query.get("c");
			const inviteCode = query.get("i");
			if (inviteCode?.length) {
				API.members.postInviteCodeRecord({ inviteCode, channelCode });
			}
		}
	}, [initialLocation, loginState]);

	const requestNativeUserInfo = () => {
		if (isTelegram) {
			return;
		}
		let promise: Promise<any> | null = null;
		if (ethereum?.isMimo) {
			promise = ethereum.request({
				method: "eth_mimo",
			});
		} else if (ethereum?.isGameTalk) {
			promise = ethereum.request({
				method: "eth_gameTalk",
			});
		}
		promise?.then((data: any) => {
			let user = data?.[0];
			dispatch(setNativeUser(user));
			if (isDef(user)) {
				const name: string = user.name;
				const avatar: string = user.avatar;
				if (isString(name)) {
					let reqData: any = {};
					reqData.nickname = name;
					if (isString(avatar) && avatar.startsWith("http")) {
						reqData.cover = avatar;
					}
					API.members.postUserUpdate(reqData);
				}
			}
		});
	};

	useEffect(() => {
		// 第一阶段取巧模式
		const handleStorageChange = (event: StorageEvent) => {
			const token = useLocalStorage.get("token");
			if (isEmpty(token)) {
				dispatch(clearUserInfo());
			}
		};
		window.addEventListener("storage", handleStorageChange);
		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, []);

	return (
		<section className="app-body">
			{isInitialized ? <AppRoutes /> : null}
		</section>
	);
};

export default App;
